import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ResponseData } from '@freelancer/datastore';
import { LoginError } from '@freelancer/login-signup';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { Focus } from '@freelancer/ui/focus';
import { HeadingType } from '@freelancer/ui/heading';
import { InputComponent, InputSize, InputType } from '@freelancer/ui/input';
import { LinkUnderline } from '@freelancer/ui/link';
import { LogoSize } from '@freelancer/ui/logo';
import { Margin } from '@freelancer/ui/margin';
import { SelectItem } from '@freelancer/ui/select';
import {
  FontColor,
  FontType,
  FontWeight,
  TextAlign,
  TextSize,
} from '@freelancer/ui/text';
import { dirtyAndValidate } from '@freelancer/ui/validators';
import { ErrorCodeApi } from 'api-typings/errors/errors';

@Component({
  selector: 'app-details-form-header',
  template: `
    <ng-content></ng-content>
  `,
})
export class DetailsFormHeaderComponent {}

@Component({
  selector: 'app-details-form',
  template: `
    <app-form-logo
      [size]="logoSize"
      [flMarginBottom]="Margin.MID"
    ></app-form-logo>
    <form class="Form" [formGroup]="formGroup" (ngSubmit)="onSubmit()">
      <fl-heading
        class="Form-heading"
        i18n="Signup details heading"
        [headingType]="HeadingType.H1"
        [size]="TextSize.MID"
        [flMarginBottom]="Margin.SMALL"
      >
        Sign Up
      </fl-heading>
      <fl-text
        i18n="Signup details description"
        [flMarginBottom]="Margin.LARGE"
        [textAlign]="TextAlign.CENTER"
        [color]="FontColor.DARK"
      >
        <ng-content select="app-details-form-header"></ng-content>
      </fl-text>
      <ng-container *ngIf="facebookSignup">
        <app-facebook-button
          i18n="Login form Facebook login button"
          [disabled]="!facebookSdkLoaded"
          [flMarginBottom]="Margin.MID"
          [stepLabel]="'DetailsForm'"
          (click)="facebookClick.emit()"
        >
          Continue With Facebook
        </app-facebook-button>
        <fl-hr
          i18n-label="Login form divider"
          label="or"
          [flMarginBottom]="Margin.MID"
        ></fl-hr>
      </ng-container>
      <fl-input
        *ngIf="formGroup.get('fullName')"
        flTrackingLabel="DetailsForm-FullNameInput"
        placeholder="Full Name"
        i18n-placeholder="Signup details full name input placeholder"
        [flMarginBottom]="Margin.SMALL"
        [control]="formGroup.get('fullName')"
        [disabled]="response === null"
        [size]="InputSize.LARGE"
        #focusInput
      ></fl-input>
      <fl-input
        *ngIf="formGroup.get('email')"
        flTrackingLabel="DetailsForm-EmailInput"
        placeholder="Email"
        i18n-placeholder="Signup details email input placeholder"
        [flMarginBottom]="Margin.SMALL"
        [control]="formGroup.get('email')"
        [disabled]="response === null"
        [size]="InputSize.LARGE"
        [type]="InputType.EMAIL"
        #focusInput
      ></fl-input>
      <fl-input
        *ngIf="formGroup.get('password')"
        flTrackingLabel="DetailsForm-PasswordInput"
        placeholder="Password"
        i18n-placeholder="Signup details password input placeholder"
        [flMarginBottom]="Margin.SMALL"
        [control]="formGroup.get('password')"
        [disabled]="response === null"
        [size]="InputSize.LARGE"
        [type]="InputType.PASSWORD"
      ></fl-input>
      <fl-select
        *ngIf="formGroup.get('country')"
        flTrackingLabel="DetailsForm-CountrySelect"
        placeholder="Select a Country"
        i18n-placeholder="Signup details country select placeholder"
        [flMarginBottom]="Margin.SMALL"
        [control]="formGroup.get('country')"
        [disabled]="response === null"
        [options]="countryOptions"
        [size]="InputSize.LARGE"
      ></fl-select>
      <fl-bit class="Agreement">
        <fl-checkbox
          flTrackingLabel="DetailsForm-TermsOfUseCheckbox"
          [control]="formGroup.get('termsOfUse')"
          [id]="'SignupAgreement'"
        ></fl-checkbox>
        <fl-label [for]="'SignupAgreement'">
          <fl-text
            i18n="Signup details signup agreement label"
            [textAlign]="TextAlign.LEFT"
            [flMarginBottom]="Margin.SMALL"
            [size]="TextSize.XXSMALL"
            [color]="
              formGroup.get('termsOfUse')!.dirty &&
              formGroup.get('termsOfUse')!.invalid
                ? FontColor.ERROR
                : FontColor.DARK
            "
          >
            <ng-container *ngIf="!partnerName; else consentWithPartnerName">
              I agree to the Freelancer
              <fl-link
                flTrackingLabel="DetailsForm-TermsOfUseLink"
                [link]="'/about/terms'"
                [newTab]="true"
                [underline]="LinkUnderline.ALWAYS"
                [size]="TextSize.INHERIT"
                >User Agreement</fl-link
              >
              and
              <fl-link
                flTrackingLabel="DetailsForm-PrivacyPolicyLink"
                [link]="'/about/privacy'"
                [newTab]="true"
                [size]="TextSize.INHERIT"
                [underline]="LinkUnderline.ALWAYS"
                >Privacy Policy</fl-link
              >.
            </ng-container>
            <ng-template #consentWithPartnerName>
              By creating an account you agree to the use of your personal
              information in accordance with {{ partnerName }} and
              Freelancer.com, through the platform's
              <fl-link
                flTrackingLabel="DetailsForm-TermsOfUseLink"
                [link]="'/about/terms'"
                [newTab]="true"
                [underline]="LinkUnderline.ALWAYS"
                [size]="TextSize.INHERIT"
                >Terms of Use</fl-link
              >
              and have read and understand the
              <fl-link
                flTrackingLabel="DetailsForm-PrivacyPolicyLink"
                [link]="'/about/privacy'"
                [newTab]="true"
                [size]="TextSize.INHERIT"
                [underline]="LinkUnderline.ALWAYS"
                >Privacy Policy</fl-link
              >
              and
              <fl-link
                flTrackingLabel="DetailsForm-CookiesPolicyLink"
                [link]="'/about/cookie'"
                [newTab]="true"
                [underline]="LinkUnderline.ALWAYS"
                [size]="TextSize.INHERIT"
                >Cookies Policy</fl-link
              >.
            </ng-template>
          </fl-text>
        </fl-label>
      </fl-bit>
      <fl-bit
        *ngIf="promotionalAgreement"
        class="Agreement"
        [flMarginBottom]="Margin.LARGE"
      >
        <fl-checkbox
          flTrackingLabel="DetailsForm-PromotionalEmailCheckbox"
          [control]="formGroup.get('receivePromotionalEmail')"
          [id]="'PromotionsAgreement'"
        ></fl-checkbox>
        <fl-label [for]="'PromotionsAgreement'">
          <fl-text
            i18n="Signup details promotional agreement checkbox"
            [textAlign]="TextAlign.LEFT"
            [flMarginBottom]="Margin.XXSMALL"
            [size]="TextSize.XXSMALL"
          >
            I would like to receive promotional email communication<fl-text
              *ngIf="partnerDomain"
              [fontType]="FontType.SPAN"
              [size]="TextSize.INHERIT"
            >
              from {{ partnerDomain }}</fl-text
            >.
          </fl-text>
        </fl-label>
      </fl-bit>
      <ng-container *ngIf="response">
        <fl-banner-alert
          *ngIf="response.status === 'error'"
          [type]="BannerAlertType.ERROR"
          [ngSwitch]="response.errorCode"
          [flMarginBottom]="Margin.SMALL"
        >
          <!-- error handling for validation -->
          <ng-container
            *ngSwitchCase="ErrorCodeApi.EMAIL_ALREADY_IN_USE"
            i18n="
               Email already in use. Provide a different email addres or sign in
              message
            "
          >
            This email address is already in use, please provide a different one
            or
            <fl-link (click)="switchToLogin.emit()">log in</fl-link>.
          </ng-container>

          <!-- error handling for facebook login / signup -->
          <ng-container
            *ngSwitchCase="ErrorCodeApi.AUTH_ACCOUNT_CLOSED"
            i18n="Login wrong username password message"
          >
            This account has been closed. Please contact
            <fl-link
              flTrackingLabel="DetailsForm-LoginClosedAccountSupportLink"
              [link]="'/support/profile/can-i-reopen-my-closed-account'"
              >support</fl-link
            >
            for further details.
          </ng-container>
          <ng-container
            *ngSwitchCase="ErrorCodeApi.AUTH_ACCOUNT_SUSPENDED"
            i18n="Login wrong username password message"
          >
            This account has been suspended. Please visit
            <fl-link
              flTrackingLabel="DetailsForm-SuspendedAccountLink"
              [link]="'/users/usersuspended'"
              >this page</fl-link
            >
            for further details.
          </ng-container>
          <ng-container
            *ngSwitchCase="ErrorCodeApi.AUTH_2FA_BLOCKED"
            i18n="Login wrong username password message"
          >
            Your login has been blocked due to signing in from an unknown
            device. Check your email and enter the code to log in.
          </ng-container>
          <ng-container
            *ngSwitchCase="ErrorCodeApi.AUTH_FACEBOOK_LINK_FAILED"
            i18n="Login facebook failed message"
          >
            Could not connect your Facebook account. Please try again.
          </ng-container>
          <ng-container
            *ngSwitchCase="ErrorCodeApi.AUTH_FACEBOOK_EMAIL_EXISTS"
            i18n="Login facebook failed message"
          >
            <!-- "again" because this error only shows up if the user goes back from FB link -->
            Your email address is already associated with a Freelancer account.
            Please log in with Facebook again and link your accounts.
          </ng-container>
          <ng-container
            *ngSwitchCase="ErrorCodeApi.AUTH_DEVICE_TOKEN_INVALID"
            i18n="Login unrecoverable error"
          >
            Something went wrong. Please refresh the page and try again, or
            contact
            <fl-link
              flTrackingLabel="DetailsForm-ErrorSupportLink"
              [link]="'/support'"
              >support</fl-link
            ><fl-text *ngIf="response.requestId">
              with request ID {{ response.requestId }}</fl-text
            >.
          </ng-container>
          <ng-container
            *ngSwitchDefault
            i18n="
               Unknown error encountered. Try again or contact support message
            "
          >
            Something went wrong while creating your account. Please try again
            or contact
            <fl-link
              flTrackingLabel="DetailsForm-ErrorSupportLink"
              [newTab]="true"
              [link]="'/support'"
              >support</fl-link
            ><fl-text *ngIf="response.requestId" [fontType]="FontType.SPAN">
              with request ID {{ response.requestId }}</fl-text
            >.
          </ng-container>
        </fl-banner-alert>
      </ng-container>
      <app-login-signup-button
        i18n="Signup details signup button"
        [busy]="response === null"
        [flMarginBottom]="Margin.LARGE"
        [stepLabel]="'DetailsForm'"
      >
        <ng-container *ngIf="partnerDomain === 'Freelancer'">
          Join {{ partnerDomain }}
        </ng-container>
        <ng-container *ngIf="partnerDomain !== 'Freelancer'">
          Sign Up to {{ partnerDomain }}
        </ng-container>
      </app-login-signup-button>
      <ng-container *ngIf="enableLoginSignupSwitch">
        <fl-hr [flMarginBottom]="Margin.SMALL"></fl-hr>
        <fl-text
          i18n="Signup details login description"
          [textAlign]="TextAlign.CENTER"
          [size]="TextSize.XXSMALL"
          [color]="FontColor.MID"
          [flMarginBottom]="Margin.XSMALL"
        >
          Already have an account<fl-text
            *ngIf="platformName"
            [color]="FontColor.MID"
            [fontType]="FontType.SPAN"
            [size]="TextSize.XXSMALL"
          >
            on
            <fl-text
              [color]="FontColor.INHERIT"
              [fontType]="FontType.SPAN"
              [size]="TextSize.INHERIT"
              [weight]="FontWeight.BOLD"
              >{{ platformName }}</fl-text
            ></fl-text
          >?
          <fl-link
            flTrackingLabel="DetailsForm-LoginLink"
            [size]="TextSize.XXSMALL"
            (click)="switchToLogin.emit()"
          >
            Log in
          </fl-link>
        </fl-text>
      </ng-container>
    </form>
  `,
  styleUrls: ['./details-form.component.scss'],
})
export class DetailsFormComponent implements AfterViewInit {
  BannerAlertType = BannerAlertType;
  ErrorCodeApi = ErrorCodeApi;
  FontColor = FontColor;
  TextSize = TextSize;
  FontType = FontType;
  FontWeight = FontWeight;
  HeadingType = HeadingType;
  InputSize = InputSize;
  InputType = InputType;
  LinkUnderline = LinkUnderline;
  LogoSize = LogoSize;
  Margin = Margin;
  TextAlign = TextAlign;

  @Input() logoSize = LogoSize.MID;
  @Input() countryOptions: ReadonlyArray<SelectItem>;
  @Input() facebookSdkLoaded = false;
  @Input() formGroup: FormGroup;
  @Input() enableLoginSignupSwitch = true;
  @Input() partnerName?: string;
  @Input() partnerDomain?: string;
  @Input() platformName?: string;
  @Input() promotionalAgreement: boolean;
  @Input() facebookSignup = false;
  @Input() response?:
    | ResponseData<unknown, ErrorCodeApi | 'UNKNOWN_ERROR'>
    | ResponseData<unknown, LoginError>;

  @Output() complete = new EventEmitter();
  @Output() facebookClick = new EventEmitter();
  @Output() switchToLogin = new EventEmitter();

  @ViewChild('focusInput') focusInput: InputComponent;

  constructor(private focus: Focus) {}

  ngAfterViewInit() {
    this.focus.focusElement(this.focusInput.nativeElement);
  }

  onSubmit() {
    if (this.response === null) {
      return;
    }
    dirtyAndValidate(this.formGroup);
    if (this.formGroup.valid) {
      this.complete.emit();
    }
  }
}
